import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import {
  aboutHeading,
  founderBio,
  workingPrinciples,
  relatedWork,
  relatedWorkIntro,
  groundedInPlace,
  financeThatServes,
} from "@/content/site";

const ABOUT_IMAGE = {
  src: "/william-martin.jpg",
  alt: "William Martin, co-founder of Bioregional Ventures, standing in a garden of young trees and vegetables.",
};

/** Phrases in the bio that should render as live external links. */
const BIO_LINKS = {};

const linkClass =
  "text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300";

/** Renders named phrases and any www.* URL in bio text as live external links. */
const linkify = (text) => {
  for (const [phrase, href] of Object.entries(BIO_LINKS)) {
    const i = text.indexOf(phrase);
    if (i !== -1) {
      return (
        <>
          {text.slice(0, i)}
          <a href={href} target="_blank" rel="noopener noreferrer" data-testid="about-bio-link" className={linkClass}>
            {phrase}
          </a>
          {text.slice(i + phrase.length)}
        </>
      );
    }
  }
  const match = text.match(/(www\.[a-z0-9.-]+\.[a-z]{2,})/i);
  if (!match) return text;
  const url = match[0];
  return (
    <>
      {text.slice(0, match.index)}
      <a
        href={`https://${url}`}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="about-bio-link"
        className={linkClass}
      >
        {url}
      </a>
      {text.slice(match.index + url.length)}
    </>
  );
};

export default function About() {
  return (
    <>
      <Seo
        title="About William Martin | Bioregional Ventures"
        description="Community wealth building through land stewardship, community ownership, project finance and local enterprise — founded by William Martin."
      />

      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-14">
          <Reveal className="md:col-span-7">
            <p className="text-xs uppercase tracking-[0.15em] text-ochre-dark mb-4">About</p>
            <h1 className="font-serif text-4xl md:text-5xl tracking-tight leading-tight text-charcoal" data-testid="about-heading">
              {aboutHeading}
            </h1>
            <div className="mt-8 space-y-6">
              {founderBio.map((p) => (
                <p key={p.slice(0, 32)} className="text-base md:text-lg leading-relaxed text-charcoal/90">
                  {linkify(p)}
                </p>
              ))}
            </div>
          </Reveal>
          <Reveal className="md:col-span-5" delay={0.1}>
            <div className="md:sticky md:top-28">
              <div className="overflow-hidden rounded-sm">
                <img
                  src={ABOUT_IMAGE.src}
                  alt={ABOUT_IMAGE.alt}
                  data-testid="about-image"
                  loading="lazy"
                  className="w-full h-72 md:h-[440px] object-cover hover:scale-[1.02] transition-transform duration-500"
                />
              </div>
              <p className="mt-4 text-xs text-charcoal/60 italic">
                William Martin, co-founder of Bioregional Ventures.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Grounded in people and place */}
      <section className="bg-sage/60 border-y border-charcoal/10">
        <div className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="grounded-heading">
              {groundedInPlace.heading}
            </h2>
            {groundedInPlace.body.map((p) => (
              <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
                {p}
              </p>
            ))}
          </Reveal>
        </div>
      </section>

      {/* Finance in service of life - bridge to Capital Partners */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-16 md:py-20">
        <Reveal>
          <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug text-charcoal" data-testid="finance-serves-heading">
            {financeThatServes.heading}
          </h2>
          {financeThatServes.body.map((p) => (
            <p key={p.slice(0, 24)} className="mt-5 text-base md:text-lg leading-relaxed text-charcoal/90">
              {p}
            </p>
          ))}
          <Link
            to="/capital-partners"
            data-testid="finance-capital-partners-link"
            className="mt-6 inline-block text-sm tracking-wide text-ochre-dark underline decoration-ochre underline-offset-4 hover:text-charcoal transition-colors duration-300"
          >
            Explore Bioregional Capital Partners
          </Link>
        </Reveal>
      </section>

      {/* Quote */}
      <section className="max-w-3xl mx-auto px-6 md:px-10 pb-16 md:pb-24 text-center">
        <Reveal>
          <blockquote data-testid="gandhi-quote">
            <p className="font-serif text-2xl md:text-3xl italic leading-snug text-charcoal">
              &ldquo;Be the change you wish to see in the world.&rdquo;
            </p>
            <footer className="mt-4 text-xs uppercase tracking-[0.2em] text-ochre-dark">Gandhi</footer>
          </blockquote>
        </Reveal>
      </section>

      <section className="bg-eucalyptus text-cream">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20 md:py-24">
          <Reveal>
            <h2 className="font-serif text-3xl md:text-4xl tracking-tight leading-snug" data-testid="principles-heading">
              Working principles
            </h2>
            <ul className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-0 divide-y md:divide-y-0 divide-cream/20">
              {workingPrinciples.map((principle, i) => (
                <li
                  key={principle}
                  className="py-5 md:border-b md:border-cream/20 text-base md:text-lg text-cream/90 flex gap-4"
                  data-testid={`principle-${i + 1}`}
                >
                  <span className="text-ochre font-serif shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  {principle}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
        <Reveal>
          <h2 className="font-serif text-2xl md:text-3xl leading-snug text-charcoal" data-testid="related-work-heading">
            Related work
          </h2>
          <p className="mt-4 text-base leading-relaxed text-charcoal/85 max-w-2xl">
            {relatedWorkIntro}
          </p>
          <div className="mt-6 flex flex-wrap gap-4" data-testid="related-work-list">
            {relatedWork.map((work) => (
              <a
                key={work.name}
                href={work.url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`related-work-${work.name.toLowerCase().replace(/\s+/g, "-")}-button`}
                className="inline-block border border-eucalyptus text-eucalyptus px-6 py-3 rounded-sm text-sm tracking-wide hover:bg-eucalyptus hover:text-cream transition-colors duration-300"
              >
                {work.name}
              </a>
            ))}
          </div>
          <Link
            to="/contact"
            data-testid="about-cta-button"
            className="mt-10 inline-block bg-eucalyptus text-cream px-7 py-3.5 rounded-sm text-sm tracking-wide hover:bg-eucalyptus-deep transition-colors duration-300"
          >
            Start a conversation
          </Link>
        </Reveal>
      </section>
    </>
  );
}
